/* Prior-Period Restatement · Phase 3a — the restated statements on the IFRS screens (IFRS.jsx and IFRSPlParts.jsx only mount
   these; the big-file rule keeps the code here).
   * RestatedProvider     reads GET /engagements/{id}/restatements-statements once per engagement (present: false without a
                          posted restatement entry — then nothing below renders and every screen looks exactly as before)
   * RestatedTag          "Restated" next to every comparative column heading (SFP, P&L, cash flow, notes)
   * RestatedPanel        the banner (comparatives restated — see note x) and the third statement of financial position at the
                          start of the comparative period (D-3 / D-11): presented / not presented with the materiality test,
                          its statement when presented, and the Partner / Manager override with a reason
   * RestatementNoteView  the restatement note (its text in the screen language and the table: as previously reported ·
                          restatement · restated · effect on the opening balances) */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { fmtNumber } from "@/components/Amount";
import { fmtDate, fmtDateTimeFirm } from "@/lib/dt";
import { toast } from "sonner";
import { asArray } from "@/lib/asArray";

const Ctx = createContext({ data: null, reload: () => {} });

export function RestatedProvider({ engId, children }) {
  const [data, setData] = useState(null);
  const reload = useCallback(() => {
    if (!engId) return;
    api.get(`/engagements/${engId}/restatements-statements`, { _silent: true })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [engId]);
  useEffect(() => { reload(); }, [reload]);
  return <Ctx.Provider value={{ data, reload }}>{children}</Ctx.Provider>;
}

export function useRestated() {
  return useContext(Ctx);
}

export function RestatedTag() {
  const { t } = useI18n();
  const { data } = useRestated();
  if (!data?.present) return null;
  return (
    <span className="ms-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold normal-case text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
          data-testid="restated-tag">{t("pyaFs.restated")}</span>
  );
}

const amt = (v) => (v == null ? "—" : fmtNumber(Number(v)));

export function RestatedPanel({ engId }) {
  const { t } = useI18n();
  const { data, reload } = useRestated();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);
  if (!data?.present) return null;
  const third = data.third_balance_sheet || {};
  const ov = third.override || data.override;
  const save = async (required) => {
    if (required !== null && !reason.trim()) { setMissing(true); toast.error(t("pyaFs.thirdReasonRequired")); return; }
    setBusy(true);
    try {
      await api.put(`/engagements/${engId}/restatements-third-balance-sheet`, { required, reason: reason.trim() });
      toast.success(t("pyaFs.thirdSaved")); setReason(""); setMissing(false); reload();
    } catch (e) { toast.error(toErrMsg(e)); } finally { setBusy(false); }
  };
  const sfp = third.sfp;
  // STEP 11d Add-on 2 · item 3: the rule decides unless an override says otherwise (the override cannot act when the third
  // statement is not available, restatement_statements.third_status)
  const decides = third.available !== false && !third.cannot_determine;
  const byOverride = !!(ov && decides && ov.required != null && Boolean(ov.required) !== Boolean(third.computed_required));
  const presentAgainstRule = !third.computed_required;     // the one override that changes the decision from the rule
  return (
    <div className="space-y-2" data-testid="restated-panel">
      <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
           data-testid="restated-banner">
        {t("pyaFs.banner").replace("{n}", String(data.note_no || ""))}{" "}
        <Link to="/fieldwork/adjusting-entries?tab=pya" className="underline">{t("pyaFs.bannerLink")}</Link>
      </div>
      <div className="rounded-md border px-4 py-3 text-xs" data-testid="third-bs">
        <div className="font-semibold">{t("pyaFs.thirdTitle").replace("{date}", fmtDate(third.as_at))}</div>
        <div className="mt-1 text-muted-foreground" data-testid="third-bs-test">
          {/* PR 52 · B1 / N-3: "not available (no prior-year ledger)" · "cannot determine — set overall materiality".
              STEP 11d Add-on 2 · item 3: the RULE in plain words — required / not required, the largest effect and the
              materiality used (no standards paragraph number) */}
          {t(third.available === false ? "pyaFs.thirdNotAvailable" : third.cannot_determine ? "pyaFs.thirdCannotDetermine"
            : third.computed_required ? "s11d6.third.ruleRequired" : "s11d6.third.ruleNotRequired")
            .replace("{eff}", amt(third.largest_effect)).replace("{om}", amt(third.overall_materiality))}
        </div>
        {decides && <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid="third-bs-why">{t("s11d6.third.ruleWhy")}</div>}
        {/* the DECISION: as the rule says, or the Partner's / Manager's override (who, when, why — also on the Audit Trail) */}
        {decides && (
          <div className={`mt-1 ${byOverride ? "text-amber-800 dark:text-amber-200" : ""}`} data-testid={byOverride ? "third-bs-override" : "third-bs-decision"}>
            {byOverride
              ? t(third.required ? "s11d6.third.presentedByOverride" : "s11d6.third.notPresentedByOverride")
                .replace("{by}", ov.by_name || "—").replace("{at}", fmtDateTimeFirm(ov.at)).replace("{reason}", ov.reason || "")
              : t(third.required ? "s11d6.third.presentedByRule" : "s11d6.third.notPresentedByRule")}
          </div>
        )}
        {ov && !byOverride && (
          <div className="mt-1 text-amber-800 dark:text-amber-200" data-testid="third-bs-override">
            {t("pyaFs.thirdOverride").replace("{by}", ov.by_name || "—").replace("{at}", fmtDateTimeFirm(ov.at)).replace("{reason}", ov.reason || "")}
          </div>
        )}
        {/* B34-6: the third statement of financial position is the third column of the SFP itself (SfpThirdColumn.jsx) */}
        {third.required && sfp && <div className="mt-1" data-testid="third-bs-as-column">{t("b34b.thirdShownAsColumn")}</div>}
        {data.can_override && decides && (   /* an override cannot act while the statement is not available / undetermined */
          <div className="mt-2 space-y-1 border-t pt-2" data-testid="third-bs-override-form">
            <div className="font-medium">{t("pyaFs.thirdOverrideTitle")}</div>
            {!byOverride && <Textarea value={reason} onChange={(e) => { setReason(e.target.value); setMissing(false); }} rows={2}
                      placeholder={t(presentAgainstRule ? "s11d6.third.reasonHint" : "s11d6.third.reasonHintOmit")} aria-invalid={missing || undefined}
                      className={missing ? "border-destructive" : ""} data-testid="third-bs-reason" />}
            <div className="flex flex-wrap gap-2">
              {/* both ways, each with a reason: present although below overall materiality (qualitative materiality, firm
                  policy) · do not present although at or above it */}
              {presentAgainstRule && !byOverride && <Button size="sm" variant="outline" disabled={busy} onClick={() => save(true)} data-testid="third-bs-present">{t("s11d6.third.presentBelow")}</Button>}
              {!presentAgainstRule && !byOverride && <Button size="sm" variant="outline" disabled={busy} onClick={() => save(false)} data-testid="third-bs-omit">{t("s11d6.third.omitAbove")}</Button>}
              {ov && <Button size="sm" variant="ghost" disabled={busy} onClick={() => save(null)} data-testid="third-bs-clear">{t("pyaFs.thirdClear")}</Button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function RestatementNoteView({ note }) {
  const colW = useResizableColumns({ storageKey: "restated-statements", defaults: [160, 130, 130, 130, 130], mins: [100, 90, 90, 90, 90] });   // Step 11d · add-on
  const { t, lang } = useI18n();
  const text = (lang === "ar" ? note.text_ar : note.text_en) || "";
  const hasOpen = asArray(note.lines).some((l) => l.effect_on_comparative_opening);
  return (
    <div className="space-y-2 text-xs" data-testid="restatement-note">
      {text.split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
      <ResizableTable cols={colW} wrap className="w-full text-xs" data-testid="restatement-note-table">
        <thead><tr className="text-[10px] uppercase text-muted-foreground">
          <ResizableTh index={0} className="py-1 text-start">{t("pyaFs.line")}</ResizableTh>
          <ResizableTh index={1} className="py-1 text-end">{t("pyaFs.asPreviouslyReported")}</ResizableTh>
          <ResizableTh index={2} className="py-1 text-end">{t("pyaFs.effect")}</ResizableTh>
          <ResizableTh index={3} className="py-1 text-end">{t("pyaFs.restated")}</ResizableTh>
          {hasOpen && <ResizableTh index={4} className="py-1 text-end" data-testid="restatement-note-opening-head">{(lang === "ar" ? note.columns_ar?.opening : note.columns?.opening) || t("pyaFs.openingEffect")}</ResizableTh>}
        </tr></thead>
        <tbody>
          {asArray(note.lines).map((l) => (
            <tr key={l.code} className="border-t" data-testid={`restatement-note-line-${l.code}`}>
              <td className="py-1"><span className="font-mono text-[10px] text-muted-foreground">{l.code}</span> {(lang === "ar" && l.title_ar) || l.title}</td>
              <td className="py-1 text-end font-mono">{amt(l.py_as_previously_reported)}</td>
              <td className="py-1 text-end font-mono">{amt(l.effect)}</td>
              <td className="py-1 text-end font-mono">{amt(l.py_restated)}</td>
              {hasOpen && <td className="py-1 text-end font-mono">{l.effect_on_comparative_opening == null ? "—" : amt(l.effect_on_comparative_opening)}</td>}
            </tr>
          ))}
        </tbody>
      </ResizableTable>
    </div>
  );
}
